import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { combineLatestWith, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Shipper } from 'src/app/models/shipper.model';
import { ShipperService } from 'src/app/services/shipper.service';
import { CountriesService } from 'src/app/services/countries.service';
import { ToastrService } from 'ngx-toastr';
import { Country } from 'src/app/models/country.model';
import { LoadingService } from 'src/app/services/loading.service';
import * as intlTelInput from 'intl-tel-input';
import { telErrorMap, telUtilsScript } from 'src/app/dashboard/helpers/tel-errors';
@Component({
  selector: 'app-edit-shipper',
  templateUrl: './edit-shipper.component.html',
  styleUrls: ['./edit-shipper.component.scss'],
})
export class EditShipperComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() shipperForEditId!: string | null | undefined;
  @Input() companyId!: string;
  @Output() modalState = new EventEmitter<{
    isDelete: boolean;
    isUpdate: boolean;
  }>();
  @Output() newShipper = new EventEmitter<Shipper>();
  textPattern = /^[\p{Letter}\p{N}\p{P}\p{S}\p{Zs}]+$/u;
  shipperForm = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.pattern(this.textPattern),
    ]),
    countryId: new FormControl('', [Validators.required]),
    city: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[\p{Letter}\p{Mark}'.,()\s-]+$/u),
    ]),
    address: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[\p{Letter}\p{Mark}0-9#'.,()\s-]+$/u),
    ]),
    province: new FormControl('', [
      Validators.pattern(/^[\p{Letter}\p{Mark}0-9#'.,()\s-]+$/u),
    ]),
    zipCode: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[\p{Letter}\p{Mark}0-9#.,()\s-]+$/u),
    ]),
    line1: new FormControl('', [Validators.pattern(this.textPattern)]),
    line2: new FormControl('', [Validators.pattern(this.textPattern)]),
    line3: new FormControl('', [Validators.pattern(this.textPattern)]),
    line4: new FormControl('', [Validators.pattern(this.textPattern)]),
    line5: new FormControl('', [Validators.pattern(this.textPattern)]),
    line6: new FormControl('', [Validators.pattern(this.textPattern)]),
    firstName: new FormControl('', [
      Validators.pattern(/^[\p{Letter}\p{Mark}'.,()\s-]+$/u),
    ]),
    lastName: new FormControl('', [
      Validators.pattern(/^[\p{Letter}\p{Mark}'.,()\s-]+$/u),
    ]),
    email: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    phone: new FormControl('', [Validators.pattern(/^[0-9]+$/)]),
  });
  shipperForEdit!: Shipper;
  countries: Country[] = [];
  countriesForSearch: Country[] = [];
  searchCountries: FormControl = new FormControl('');
  userTelData: any;
  telError!: string;
  isTelCustomError!: boolean;
  private destroy = new Subject<boolean>();

  constructor(
    private shipperService: ShipperService,
    private countriesService: CountriesService,
    private toastr: ToastrService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.getCountries();
    this.searchCountries.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: (value: string) => {
        this.countries = this.countriesForSearch.filter((c: Country) =>
          c.name.toLowerCase().includes(value.toLowerCase()),
        );
      },
    });
    this.getShipper();
  }

  ngAfterViewInit(): void {
    this.createPhoneElement();
  }

  listenFormChanges() {
    if (!this.shipperForm.controls.line1.value) {
      this.shipperForm.controls.name.valueChanges
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (value) => {
            this.shipperForm.controls.line1.patchValue(value);
          },
        });
    }

    if (!this.shipperForm.controls.line2.value) {
      this.shipperForm.controls.address.valueChanges
        .pipe(
          combineLatestWith(this.shipperForm.controls.city.valueChanges),
          takeUntil(this.destroy),
        )
        .subscribe({
          next: ([address, city]) => {
            this.shipperForm.controls.line2.patchValue(`${address}, ${city}`);
          },
        });
    }

    if (!this.shipperForm.controls.line3.value) {
      this.shipperForm.controls.province.valueChanges
        .pipe(
          combineLatestWith(
            this.shipperForm.controls.countryId.valueChanges,
            this.shipperForm.controls.zipCode.valueChanges,
          ),
          takeUntil(this.destroy),
        )
        .subscribe({
          next: ([province, countryId, zipCode]) => {
            this.shipperForm.controls.line3.patchValue(
              `${province}, ${
                this.getCountryById(countryId ?? '')?.name
              }, ${zipCode}`,
            );
          },
        });
    }
  }

  createPhoneElement() {
    const telInputEl = document.getElementById('phone');

    if (telInputEl) {
      this.userTelData = intlTelInput(telInputEl, {
        initialCountry: 'US',
        separateDialCode: true,
        utilsScript: telUtilsScript,
      });
    }
  }

  validateTel() {
    if (!this.userTelData.isPossibleNumber()) {
      this.isTelCustomError = true;
      this.telError = telErrorMap[this.userTelData.getValidationError()];

      if (!this.telError) {
        this.telError = 'Invalid number';
      }
    } else {
      this.isTelCustomError = false;
    }
  }

  getCountries() {
    this.countriesService.contriesList.pipe(takeUntil(this.destroy)).subscribe({
      next: (resp: Country[] | null) => {
        if (resp) {
          this.countries = resp.sort((a: Country, b: Country) => {
            return a.name.toLowerCase() < b.name.toLowerCase()
              ? -1
              : a.name.toLowerCase() > b.name.toLowerCase()
                ? 1
                : 0;
          });
          this.countriesForSearch = resp;
        }
      },
    });
  }

  getCountryById(id: string): Country | undefined {
    return this.countries.find((c) => c.id === id);
  }

  getShipper() {
    if (this.shipperForEditId) {
      this.loadingService.changeLoadingStatus(true);
      this.shipperService
        .getShipper(this.shipperForEditId)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (resp: Shipper) => {
            this.shipperForEdit = resp;
            this.patchShipperForm();
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.loadingService.changeLoadingStatus(false);
          },
        });
    } else {
      this.listenFormChanges();
    }
  }

  patchShipperForm() {
    if (this.shipperForEdit) {
      this.shipperForm.patchValue({
        name: this.shipperForEdit.name,
        countryId: this.shipperForEdit.countryId,
        city: this.shipperForEdit.city,
        address: this.shipperForEdit.address,
        province: this.shipperForEdit.province,
        zipCode: this.shipperForEdit.zipCode,
        line1: this.shipperForEdit.line1
          ? this.shipperForEdit.line1
          : this.shipperForEdit.name,
        line2: this.shipperForEdit.line2
          ? this.shipperForEdit.line2
          : `${this.shipperForEdit.address}, ${this.shipperForEdit.city}`,
        line3: this.shipperForEdit.line3
          ? this.shipperForEdit.line3
          : `${this.shipperForEdit.province}, ${
              this.getCountryById(this.shipperForEdit.countryId)?.name
            }, ${this.shipperForEdit.zipCode}`,
        line4: this.shipperForEdit.line4,
        line5: this.shipperForEdit.line5,
        line6: this.shipperForEdit.line6,
        firstName: this.shipperForEdit.firstName,
        lastName: this.shipperForEdit.lastName,
        email: this.shipperForEdit.email,
      });
      if (this.shipperForEdit.phone) {
        this.userTelData.setNumber(this.shipperForEdit.phone);
      }
    }
  }

  deleteShipper() {
    if (this.shipperForEdit && this.shipperForEdit.id) {
      this.loadingService.changeLoadingStatus(true);
      this.shipperService
        .deleteShipper(this.shipperForEdit.id)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: () => {
            this.modalState.emit({ isDelete: false, isUpdate: true });
            this.toastr.success('Shipper successfully deleted', 'Success');
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.loadingService.changeLoadingStatus(false);
          },
        });
    }
  }

  addNewShipper() {
    if (this.shipperForm.invalid) {
      this.shipperForm.markAllAsTouched();
      return;
    }
    this.loadingService.changeLoadingStatus(true);
    const payload: Partial<Shipper> = {
      name: this.shipperForm.controls.name.value ?? '',
      companyId: this.companyId,
      countryId: this.shipperForm.controls.countryId.value ?? '',
      city: this.shipperForm.controls.city.value ?? '',
      address: this.shipperForm.controls.address.value ?? '',
      province: this.shipperForm.controls.province.value ?? '',
      zipCode: this.shipperForm.controls.zipCode.value ?? '',
      line1: this.shipperForm.controls.line1.value
        ? this.shipperForm.controls.line1.value
        : (this.shipperForm.controls.name.value ?? ''),
      line2: this.shipperForm.controls.line2.value
        ? this.shipperForm.controls.line2.value
        : `${this.shipperForm.controls.address.value}, ${this.shipperForm.controls.city.value}`,
      line3: this.shipperForm.controls.line3.value
        ? this.shipperForm.controls.line3.value
        : `${this.shipperForm.controls.province.value}, ${
            this.getCountryById(
              this.shipperForm.controls.countryId.value || '',
            )?.name
          }, ${this.shipperForm.controls.zipCode.value}`,
      line4: this.shipperForm.controls.line4.value ?? '',
      line5: this.shipperForm.controls.line5.value ?? '',
      line6: this.shipperForm.controls.line6.value ?? '',
      firstName: this.shipperForm.controls.firstName.value ?? '',
      lastName: this.shipperForm.controls.lastName.value ?? '',
      email: this.shipperForm.controls.email.value ?? '',
      phone: this.userTelData.getNumber() ?? '',
    };
    if (!this.shipperForEdit) {
      this.shipperService
        .addShipper(payload)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (resp: Shipper) => {
            this.toastr.success('Shipper successfully added', 'Success');
            this.shipperForm.reset();
            this.modalState.emit({ isDelete: false, isUpdate: true });
            this.newShipper.emit(resp);
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.modalState.emit({ isDelete: false, isUpdate: false });
            this.loadingService.changeLoadingStatus(false);
          },
        });
    } else {
      payload.id = this.shipperForEdit.id;
      this.shipperService
        .updateShipper(this.shipperForEdit.id, payload)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: () => {
            this.toastr.success('Shipper successfully updated', 'Success');
            this.shipperForm.reset();
            this.modalState.emit({ isDelete: false, isUpdate: true });
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.modalState.emit({ isDelete: false, isUpdate: false });
            this.loadingService.changeLoadingStatus(false);
          },
        });
    }
  }

  closeModal() {
    this.modalState.emit({ isDelete: false, isUpdate: false });
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
